import React, { useState, useEffect } from 'react';
import { config } from '../config';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import Spinner from '../components/Spinner';
import CustomerChat from '../components/CustomerChat';
import './CustomerPortal.css';

function CustomerPortal() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [appointmentForm, setAppointmentForm] = useState({
    date: '',
    time: '',
    description: '',
    carId: ''
  });
  const [appointments, setAppointments] = useState([]);

  useEffect(() => {
    if (token) {
      loadData();
      loadAppointments();
    }
  }, [token]);

  const loadData = async () => {
    try {
      // Ensure token is properly encoded for URL
      const encodedToken = encodeURIComponent(token);
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/customer/${encodedToken}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Ungültiger Zugangscode');
      }
      const customerData = await response.json();
      setData(customerData);
      if (customerData.cars.length > 0 && !appointmentForm.carId) {
        setAppointmentForm(prev => ({ ...prev, carId: customerData.cars[0].id }));
      }
    } catch (error) {
      console.error('Error loading customer data:', error);
      showNotification(error.message || 'Fehler beim Laden der Daten', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAppointments = async () => {
    try {
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/appointments/public/${token}`);
      if (response.ok) {
        const apts = await response.json();
        setAppointments(apts);
      }
    } catch (error) {
      console.error('Error loading appointments:', error);
    }
  };

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/appointments/public/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(appointmentForm)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fehler beim Erstellen des Termins');
      }

      const newAppointment = await response.json();
      setAppointments([...appointments, newAppointment]);
      setShowAppointmentForm(false);
      setAppointmentForm({
        date: '',
        time: '',
        description: '',
        carId: data?.cars[0]?.id || ''
      });
      showNotification('Termin erfolgreich gebucht! Bitte warten Sie auf die Bestätigung.', 'success');
    } catch (error) {
      showNotification(error.message || 'Fehler beim Erstellen des Termins', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Ausstehend',
      confirmed: 'Bestätigt',
      cancelled: 'Abgesagt'
    };
    return labels[status] || status;
  };

  const getStatusClass = (status) => {
    const classes = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      cancelled: 'status-cancelled'
    };
    return classes[status] || '';
  };

  if (loading) {
    return <div className="customer-portal-loading"><Spinner /></div>;
  }

  if (!data) {
    return <div className="customer-portal-error">Ungültiger Zugangscode</div>;
  }

  return (
    <div className="customer-portal">
      <div className="customer-portal-header">
        <h1>Kundenportal</h1>
        <p>Willkommen, {data.customer.firstName} {data.customer.lastName}</p>
      </div>

      <div className="customer-portal-tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Übersicht
        </button>
        <button
          className={activeTab === 'cars' ? 'active' : ''}
          onClick={() => setActiveTab('cars')}
        >
          Fahrzeuge
        </button>
        <button
          className={activeTab === 'repairs' ? 'active' : ''}
          onClick={() => setActiveTab('repairs')}
        >
          Reparaturen
        </button>
        <button
          className={activeTab === 'invoices' ? 'active' : ''}
          onClick={() => setActiveTab('invoices')}
        >
          Rechnungen
        </button>
        <button
          className={activeTab === 'appointments' ? 'active' : ''}
          onClick={() => setActiveTab('appointments')}
        >
          Termine
        </button>
        <button
          className={activeTab === 'chat' ? 'active' : ''}
          onClick={() => setActiveTab('chat')}
        >
          Nachricht
        </button>
      </div>

      <div className="customer-portal-content">
        {activeTab === 'overview' && (
          <div className="portal-section">
            <h2>Ihre Daten</h2>
            <div className="info-grid">
              <div className="info-item">
                <strong>Fahrzeuge:</strong> {data.cars.length}
              </div>
              <div className="info-item">
                <strong>Reparaturen:</strong> {data.repairs.length}
              </div>
              <div className="info-item">
                <strong>Rechnungen:</strong> {data.invoices.length}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'cars' && (
          <div className="portal-section">
            <h2>Ihre Fahrzeuge</h2>
            {data.cars.length === 0 ? (
              <p>Keine Fahrzeuge vorhanden</p>
            ) : (
              <div className="cars-list">
                {data.cars.map(car => (
                  <div key={car.id} className="car-card">
                    <h3>{car.make} {car.model} {car.year}</h3>
                    <p>Kennzeichen: {car.licensePlate || '-'}</p>
                    {car.mileage && <p>Kilometerstand: {car.mileage.toLocaleString('de-DE')} km</p>}
                    {car.repairs && car.repairs.length > 0 && (
                      <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e0e0e0' }}>
                        <h4 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Reparatur-Historie</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {car.repairs.map(repair => (
                            <div key={repair.id} style={{ padding: '0.5rem', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <strong>{repair.repairNumber || repair.id}</strong>
                                <span className={`status-badge ${repair.status}`}>
                                  {repair.status === 'pending' ? 'Ausstehend' : 
                                   repair.status === 'in_progress' ? 'In Bearbeitung' : 
                                   'Abgeschlossen'}
                                </span>
                              </div>
                              {repair.description && <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>{repair.description}</p>}
                              <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', color: '#666' }}>
                                <span>Datum: {formatDate(repair.date)}</span>
                                {repair.mileage && <span>Kilometerstand: {repair.mileage.toLocaleString('de-DE')} km</span>}
                                {repair.totalAmount && <span><strong>Betrag: {repair.totalAmount.toFixed(2)} €</strong></span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="portal-section">
            <h2>Ihre Rechnungen</h2>
            {data.invoices.length === 0 ? (
              <p>Keine Rechnungen vorhanden</p>
            ) : (
              <div className="invoices-list">
                {data.invoices.map(invoice => (
                  <div key={invoice.id} className="invoice-card">
                    <div className="invoice-header">
                      <h3>{invoice.invoiceNumber || invoice.id}</h3>
                      <span className={`status-badge ${invoice.status}`}>
                        {invoice.status === 'pending' ? 'Ausstehend' : 
                         invoice.status === 'sent' ? 'Versendet' : 
                         invoice.status === 'paid' ? 'Bezahlt' : 
                         invoice.status === 'overdue' ? 'Überfällig' : 
                         invoice.status}
                      </span>
                    </div>
                    <p>Datum: {formatDate(invoice.date)}</p>
                    {invoice.dueDate && <p>Fälligkeitsdatum: {formatDate(invoice.dueDate)}</p>}
                    <p><strong>Betrag: {invoice.totalAmount?.toFixed(2) || '0.00'} €</strong></p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'repairs' && (
          <div className="portal-section">
            <h2>Ihre Reparaturen</h2>
            {data.repairs.length === 0 ? (
              <p>Keine Reparaturen vorhanden</p>
            ) : (
              <div className="repairs-list">
                {data.repairs.map(repair => {
                  const car = data.cars.find(c => c.id === repair.carId);
                  return (
                    <div key={repair.id} className="repair-card">
                      <div className="repair-header">
                        <h3>{repair.repairNumber || repair.id}</h3>
                        <span className={`status-badge ${repair.status}`}>
                          {repair.status === 'pending' ? 'Ausstehend' : 
                           repair.status === 'in_progress' ? 'In Bearbeitung' : 
                           'Abgeschlossen'}
                        </span>
                      </div>
                      {repair.description && <p>{repair.description}</p>}
                      <p>Datum: {formatDate(repair.date)}</p>
                      {car && <p>Fahrzeug: {car.make} {car.model}</p>}
                      {repair.totalAmount && (
                        <p><strong>Betrag: {repair.totalAmount.toFixed(2)} €</strong></p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'appointments' && (
          <div className="portal-section">
            <h2>Terminbuchung</h2>
            <button
              className="btn-primary"
              onClick={() => setShowAppointmentForm(true)}
              style={{ marginBottom: '1rem' }}
            >
              Neuen Termin buchen
            </button>

            {showAppointmentForm && (
              <div className="appointment-form-modal">
                <div className="modal-content">
                  <h3>Termin buchen</h3>
                  <form onSubmit={handleAppointmentSubmit}>
                    <div className="form-group">
                      <label>Fahrzeug</label>
                      <select
                        value={appointmentForm.carId}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, carId: e.target.value })}
                        required
                      >
                        <option value="">Bitte wählen</option>
                        {data.cars.map(car => (
                          <option key={car.id} value={car.id}>
                            {car.make} {car.model} {car.year} ({car.licensePlate})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Datum</label>
                      <input
                        type="date"
                        value={appointmentForm.date}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Uhrzeit</label>
                      <input
                        type="time"
                        value={appointmentForm.time}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, time: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Beschreibung (optional)</label>
                      <textarea
                        value={appointmentForm.description}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, description: e.target.value })}
                        rows="3"
                        placeholder="Beschreiben Sie kurz das Problem..."
                      />
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="btn-primary">
                        Termin buchen
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => {
                          setShowAppointmentForm(false);
                          setAppointmentForm({
                            date: '',
                            time: '',
                            description: '',
                            carId: data.cars[0]?.id || ''
                          });
                        }}
                      >
                        Abbrechen
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <h3 style={{ marginTop: '2rem' }}>Ihre Termine</h3>
            {appointments.length === 0 ? (
              <p>Keine Termine vorhanden</p>
            ) : (
              <div className="appointments-list">
                {appointments.map(apt => {
                  const car = data.cars.find(c => c.id === apt.carId);
                  return (
                    <div key={apt.id} className="appointment-card">
                      <div className="appointment-header">
                        <h4>{formatDateTime(apt.dateTime)}</h4>
                        <span className={`status-badge ${getStatusClass(apt.status)}`}>
                          {getStatusLabel(apt.status)}
                        </span>
                      </div>
                      {car && <p>Fahrzeug: {car.make} {car.model}</p>}
                      {apt.description && <p>{apt.description}</p>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="portal-section">
            <CustomerChat token={token} customerId={data?.customer?.id} tenantId={data?.tenantId} />
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerPortal;

